import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
	Accordion,
	AccordionItem,
	AccordionItemHeading,
	AccordionItemButton,
	AccordionItemPanel,
} from "react-accessible-accordion";

import AppText from "../../../../components/Core/AppText/AppText";
import { History } from "../../../../models/RecruitmentHistory";
import { appTheme } from "../../../../theme/Theme";
import SecondLevel from "./SecondLevel";
import { getHistoryMetadataString } from "../../../../utils/recruitment.utils";
import { RecruitmentStatusType } from "../../../../configs/constants/RecruitmentStatusType";
import { ICON_TYPE } from "../../../../configs/constants/IconsTypes";

interface FirstLevelProps {
	statusItems: History[];
	id: string;
	preExpandedIds: string[];
}

const FirstLevel: React.FC<FirstLevelProps> = ({ statusItems, id, preExpandedIds }) => {
	const { t } = useTranslation();

	return (
		<TreeViewStyles key={id}>
			{statusItems.map(({ data, date, id, items, user }, statusIndex) => {
				const status = data?.status!; // if we got this far it must exist

				return (
					<Accordion allowMultipleExpanded allowZeroExpanded preExpanded={preExpandedIds} key={id}>
						<AccordionItem uuid={id} key={id}>
							<AccordionItemHeading className="accordion__heading accordion__heading-main">
								<AccordionItemButton>
									<AppText size="m" weight="bold" color="black">
										{t(status as any)}
									</AppText>
									<DateText>
										{getHistoryMetadataString(date, user?.firstName, user?.lastName)}
									</DateText>
								</AccordionItemButton>
							</AccordionItemHeading>
							<AccordionItemPanel>
								{(items || []).map((actionItem, actionIndex) => {
									let icon = ICON_TYPE.GREEN_CHECK_CIRCLE;

									if (statusIndex === 0 && actionIndex === 0) {
										if (status === RecruitmentStatusType.IN_REVIEW)
											icon = ICON_TYPE.BLUE_HORIZONTAL_DOTS_CIRCLE;
										else if (status === RecruitmentStatusType.CANCELLED)
											icon = ICON_TYPE.RED_CROSS_CIRCLE;
									}

									return <SecondLevel actionItem={actionItem} icon={icon} key={actionItem.id} />;
								})}
							</AccordionItemPanel>
						</AccordionItem>
					</Accordion>
				);
			})}
		</TreeViewStyles>
	);
};

export default FirstLevel;

const TreeViewStyles = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	min-width: 415px;
	padding-left: 8px;

	&::after {
		content: "";
		width: 1px;
		background: ${appTheme.colors.COLOR_GRAY_TEXT_LIGHT};
		position: absolute;
		top: 0;
		height: 100%;
		z-index: 1;
		left: 26px;
	}

	.accordion__button {
		position: relative;
		min-height: 50px;
		width: fit-content;
		background-repeat: no-repeat !important;
		background-position: right 6px !important;
		padding-right: 20px;

		&:hover {
			cursor: pointer;
		}
		&[aria-expanded="true"] {
			background: url("/svg/sorting-arrow-up.svg");
		}
		&[aria-expanded="false"] {
			background: url("/svg/sorting-arrow-down.svg");
		}
		&.action_item-button {
			background-position: right 14%;
			margin-top: 30px;
		}
	}

	.status-no-actions {
		min-height: 50px;
		padding-top: 8px;
	}

	.actions_sub-panel {
		margin-top: 12px;

		.actions_sub-wrapper {
			margin-bottom: 30px;
			&:last-child {
				margin-bottom: 0;
			}
		}

		.actions_sub-wrapper-child {
			width: 294px;
		}
	}

	.accordion__panel {
		.action_main-wrapper {
			margin-top: 40px;
			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	.accordion__item {
		.actions_sub-panel {
		}
	}

	.accordion {
		margin-bottom: 35px;
		&:last-child {
			margin-bottom: 0;
			.accordion__panel {
				.action_main-wrapper:last-child {
					margin-bottom: 0;
				}
			}
		}
	}

	.accordion__heading-main {
		padding-top: 8px;
	}

	.accordion__heading-main,
	.accordion:last-child .accordion__panel .action_main-wrapper:last-child,
	.accordion__panel .accordion:last-child,
	.status-no-actions {
		position: relative;
		z-index: 2;
		background: ${appTheme.colors.COLOR_LIGHT_GRAY};
	}
`;

const DateText = styled.p`
	position: absolute;
	left: 0;
	top: 23px;
	color: ${appTheme.colors.COLOR_GRAY_TEXT_LIGHT};
	font-size: 0.75rem;
	width: max-content;

	&.actions_icons-date {
		left: 54px;
		top: 24px;
	}
`;
